'use client';

import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { EqualsIcon, TargetIcon, TrendDownIcon, TrendUpIcon } from '@phosphor-icons/react';

import { EChart, type EChartsOption } from '@/components/charts/EChart';
import { buildTooltip } from '@/components/charts/EChartTooltip';
import { useCompactCurrency } from '@/hooks/useCompactCurrency';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { useAvailableToSpend } from '@/hooks/queries/useAvailableToSpend';
import {
  selectPaceChart,
  useSpendingPace,
  type PaceChartModel,
} from '@/hooks/queries/useSpendingPace';
import { hideOverlapLabels } from '@/lib/charts/axis';
import type { ThemePalette } from '@/lib/theme/tokens';
import { useThemeColors } from '@/lib/theme/useThemeColors';
import { cn } from '@/lib/utils';
import type { SpendingPace } from '@/types';
import { BASELINE_LABEL, dailyMetaParts, selectDailyMeta } from './dailyMeta';
import { WidgetCard, widgetState } from './WidgetCard';
import { WidgetInfoButton, WidgetInfoRow } from './WidgetInfoButton';

/** Matches `--font-mono` in globals.css — ECharts can't read a CSS class. */
const CHART_MONO = "'DM Mono', ui-monospace, SFMono-Regular, monospace";

/**
 * W2 · Ritmo de gastos — cumulative spend so far vs the reference curve (the
 * 6-month day-of-month average, or a straight line when there is no history).
 * The headline is spent-to-date + a chip carrying the % difference vs. the
 * SAME-PERIOD previsto (the dashed curve's value at "hoje"), so the comparison
 * reads apples-to-apples. The absolute previsto and the month-scale reference
 * live in the header's ⓘ popover, keeping the card lean. Below: a legend row,
 * an area chart and a daily-budget footer. The chart tells two stories in two
 * colors ("você" vs. the reference): the primary solid line is the real spend
 * (stops at "hoje") and continues as a primary dotted step line of the known
 * recurring charges (each charge day carries a label chip); the reference
 * curve is a neutral gray dashed line, edge-to-edge, with no area. The same
 * component is the desktop card and the mobile Panorama slide — `sm:`
 * variants switch legend/axis/footer detail on and off.
 */
export function RitmoWidget(): ReactNode {
  const { data, error, isLoading } = useSpendingPace();

  return (
    <WidgetCard
      eyebrow="Ritmo de gastos"
      info={
        data ? (
          <WidgetInfoButton widget="ritmo" label="Como o ritmo é calculado">
            <RitmoInfo data={data} />
          </WidgetInfoButton>
        ) : undefined
      }
      action={{ label: 'Ver', href: '/transactions' }}
      state={widgetState(data, error, isLoading, isPaceEmpty)}
    >
      <RitmoBody data={data} />
    </WidgetCard>
  );
}

function isPaceEmpty(data: SpendingPace): boolean {
  return data.series.length === 0 && data.spentToDate === 0;
}

/** Same-period comparison: what the reference curve says you'd have spent BY
 * TODAY (the dashed line's value at "hoje"), not the month total. */
function expectedToDateOf(data: SpendingPace): number {
  const index = Math.min(data.baselineSeries.length, Math.max(1, data.daysElapsed)) - 1;
  return data.baselineSeries[index] ?? 0;
}

/**
 * How the reference curve was built, per source. "projection" (no history)
 * is the API's fallback: the real series up to today continued by the
 * recurring-charge projection — the same points the chart already draws.
 */
const BASELINE_CURVE_COPY: Record<SpendingPace['baselineSource'], string> = {
  average: 'a média diária dos últimos 6 meses, acumulada pelo mês',
  budget: 'o orçamento do mês distribuído em linha reta',
  projection: 'o gasto até hoje mais essas cobranças',
};

/**
 * The ⓘ glossary (design review): the card headline keeps only gasto + the %
 * chip; previsto and the month reference moved here so the numbers don't
 * crowd the card.
 */
function RitmoInfo({ data }: { data: SpendingPace }): ReactNode {
  const formatCurrency = useFormatCurrency();
  const compactCurrency = useCompactCurrency({ style: 'mil', maximumFractionDigits: 2 });

  return (
    <div className="space-y-2.5">
      <p className="text-sm font-medium text-text-primary">Como ler o ritmo</p>
      <dl className="space-y-1">
        <WidgetInfoRow
          label="Gasto até hoje"
          value={formatCurrency(data.spentToDate, { noCents: true })}
        />
        <WidgetInfoRow
          label="Previsto até hoje"
          value={formatCurrency(expectedToDateOf(data), { noCents: true })}
        />
        <WidgetInfoRow
          label={`Referência do mês (${BASELINE_LABEL[data.baselineSource]})`}
          value={compactCurrency(data.baselineTotal)}
        />
      </dl>
      <p className="text-xs text-text-tertiary">
        O selo compara o gasto com o previsto até hoje. No gráfico, a linha pontilhada são as
        cobranças recorrentes previstas até o fim do mês
        {data.baselineSource === 'projection'
          ? `; a referência (${BASELINE_CURVE_COPY.projection}) coincide com ela e fica oculta.`
          : `; a linha tracejada cinza é ${BASELINE_CURVE_COPY[data.baselineSource]}.`}
      </p>
    </div>
  );
}

function RitmoBody({ data }: { data: SpendingPace | undefined }): ReactNode {
  const formatCurrency = useFormatCurrency();
  const palette = useThemeColors();
  // W1's payload feeds the cross-widget daily meta (SWR dedupes with the hero's
  // own fetch — no extra request on the home page).
  const { data: available } = useAvailableToSpend();

  const model = useMemo(() => (data ? selectPaceChart(data) : null), [data]);
  // Under the projection fallback the API's baselineSeries is the real series
  // continued by the projection (point for point) — the dashed gray line would
  // sit exactly on the solid and dotted ones, so it and its legend item are
  // dropped.
  const baseline = useMemo<BaselineLine | null>(
    () =>
      data && data.baselineSource !== 'projection'
        ? { label: BASELINE_LABEL[data.baselineSource] }
        : null,
    [data],
  );
  const desktopOption = useMemo<EChartsOption>(
    () =>
      model ? buildPaceOption(model, (v) => formatCurrency(v), palette, 'desktop', baseline) : {},
    [model, formatCurrency, palette, baseline],
  );
  const mobileOption = useMemo<EChartsOption>(
    () =>
      model ? buildPaceOption(model, (v) => formatCurrency(v), palette, 'mobile', baseline) : {},
    [model, formatCurrency, palette, baseline],
  );

  if (!data || !model) return null;

  const expectedToDate = expectedToDateOf(data);

  // The chip carries the difference vs. previsto as a percentage (design
  // review) — the absolute previsto/reference numbers live in the ⓘ popover.
  // A 0% difference (common under the projection fallback, whose baseline
  // derives from the same spend) reads "no ritmo" instead of an arbitrary
  // direction. Equality is checked first so a zero previsto (the average
  // curve stays flat until the month's first usual charge) with zero spend
  // also reads "no ritmo"; only spend against a zero previsto has no ratio.
  const pctDiff =
    data.spentToDate === expectedToDate
      ? 0
      : expectedToDate > 0
        ? Math.round(Math.abs(data.spentToDate / expectedToDate - 1) * 100)
        : null;
  const chip =
    pctDiff === 0
      ? { label: 'no ritmo', className: 'bg-success-surface text-success-text', Icon: EqualsIcon }
      : data.isAbovePace
        ? { label: 'acima', className: 'bg-warning-surface text-warning-text', Icon: TrendUpIcon }
        : { label: 'abaixo', className: 'bg-success-surface text-success-text', Icon: TrendDownIcon };
  const chipLabel = pctDiff ? `${pctDiff}% ${chip.label}` : chip.label;
  const ChipIcon = chip.Icon;

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-[1.8rem] leading-none font-semibold tracking-tight text-text-primary tabular-nums sm:text-4xl">
          {formatCurrency(data.spentToDate, { noCents: true })}
        </span>
        <span
          className={cn(
            'inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium',
            chip.className,
          )}
        >
          <ChipIcon size={12} weight="fill" aria-hidden />
          {chipLabel}
        </span>
      </div>

      <p className="mt-1 text-[11px] text-text-tertiary sm:text-xs">
        gasto até hoje vs. o previsto até hoje
      </p>

      <ChartLegend baseline={baseline} />

      <div className="mt-auto pt-3">
        <div className="hidden h-40 sm:block">
          <EChart option={desktopOption} />
        </div>
        <div className="h-24 sm:hidden">
          <EChart option={mobileOption} />
        </div>
      </div>

      <DailyMetaFooter data={data} available={available} />
    </div>
  );
}

/**
 * Desktop footer — the shared min(pace, liquidity) daily meta (see dailyMeta.ts).
 * Deficit states replace the allowance line entirely: pace exhausted reads as a
 * warning, a negative window (Disponível below zero) as danger.
 */
function DailyMetaFooter({
  data,
  available,
}: {
  data: SpendingPace;
  available: ReturnType<typeof useAvailableToSpend>['data'];
}): ReactNode {
  const formatCurrency = useFormatCurrency();
  const meta = selectDailyMeta(data, available);
  if (!meta) return null;
  const parts = dailyMetaParts(meta, formatCurrency);

  return (
    <footer className="mt-3 hidden items-center gap-3 border-t border-border-subtle pt-3 sm:flex">
      <span
        className={cn(
          'flex size-[34px] shrink-0 items-center justify-center rounded-full',
          parts.tone === 'ok' && 'bg-brand-50 text-primary',
          parts.tone === 'warning' && 'bg-warning-surface text-warning-text',
          parts.tone === 'danger' && 'bg-error-surface text-error-text',
        )}
        aria-hidden
      >
        <TargetIcon size={16} weight="fill" />
      </span>
      <div className="min-w-0">
        <p className="text-xs text-text-tertiary">{parts.title}</p>
        <p className="text-sm font-bold text-text-primary">
          {parts.lead}
          {parts.amount !== null && (
            <span
              className={cn(
                'font-mono tabular-nums',
                parts.tone === 'ok' && 'text-primary',
                parts.tone === 'warning' && 'text-warning-text',
                parts.tone === 'danger' && 'text-error-text',
              )}
            >
              {parts.amount}
            </span>
          )}
          {parts.trail}
        </p>
      </div>
    </footer>
  );
}

/** The reference line's user-facing name ("média 6m", "orçamento"); null = line hidden. */
interface BaselineLine {
  label: string;
}

/**
 * Legend row (desktop only — the mobile Panorama slide has no vertical room
 * and its mini chart is a glanceable sparkline). Swatches mirror the lines:
 * primary solid, primary dotted, gray dashed. The labels stay in the
 * accessibility tree (the canvas chart has no legend of its own); only the
 * decorative swatches are hidden.
 */
function ChartLegend({ baseline }: { baseline: BaselineLine | null }): ReactNode {
  return (
    <div className="mt-3.5 hidden gap-3.5 text-[11px] text-text-tertiary sm:flex">
      <span className="inline-flex items-center gap-1.5">
        <i className="inline-block w-[18px] border-t-2 border-solid border-primary" aria-hidden />
        você
      </span>
      <span className="inline-flex items-center gap-1.5">
        <i className="inline-block w-[18px] border-t-2 border-dotted border-primary" aria-hidden />
        cobranças previstas
      </span>
      {baseline && (
        <span className="inline-flex items-center gap-1.5">
          <i
            className="inline-block w-[18px] border-t-2 border-dashed border-text-tertiary"
            aria-hidden
          />
          {baseline.label}
        </span>
      )}
    </div>
  );
}

/** "média 6m" → "Média 6m" for the tooltip row. */
function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * One option builder for both breakpoints: the mobile mini chart is the same
 * series with axes, gridlines, label chips and the "hoje" markLine stripped.
 */
function buildPaceOption(
  model: PaceChartModel,
  format: (value: number) => string,
  pal: ThemePalette,
  variant: 'desktop' | 'mobile',
  baseline: BaselineLine | null,
): EChartsOption {
  const mini = variant === 'mobile';

  // Each charge day carries its first label as a surface-colored chip (a
  // backgroundColor chip, not a textBorder halo — the halo lets the crossing
  // line show through the spaces) and a dot ringed in surface.
  const projectionData = model.projectionSeries.map((value, i) => {
    const label = model.projectionLabels[i]?.[0];
    if (mini || value === null || !label) return value;
    return {
      value,
      symbol: 'circle',
      symbolSize: 6,
      label: {
        show: true,
        formatter: label,
        position: 'top' as const,
        fontFamily: CHART_MONO,
        fontSize: 9,
        color: pal.textSecondary,
        backgroundColor: pal.surface,
        padding: [2, 4],
        borderRadius: 3,
      },
    };
  });

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'line', lineStyle: { color: pal.border, type: 'dashed' } },
      // The projection day's charges ride under the rows (empty before today).
      formatter: buildTooltip({ format, notes: (i) => model.projectionLabels[i] ?? [] }),
    },
    grid: mini
      ? { left: 0, right: 0, top: 4, bottom: 0 }
      : { left: 4, right: 4, top: 14, bottom: 0, containLabel: true },
    xAxis: {
      type: 'category',
      data: model.dayLabels,
      boundaryGap: false,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: mini
        ? { show: false }
        : {
            fontFamily: CHART_MONO,
            fontSize: 10,
            color: pal.textTertiary,
            // 0-based interval 6 → 01, 08, 15, 22, 29 (roughly weekly).
            interval: 6,
          },
    },
    yAxis: {
      type: 'value',
      max: model.yMax,
      axisLabel: { show: false },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: mini
        ? { show: false }
        : { show: true, lineStyle: { color: pal.border, type: [3, 3] } },
    },
    series: [
      {
        name: 'Gasto',
        type: 'line',
        data: model.realSeries,
        smooth: true,
        connectNulls: false,
        symbol: 'circle',
        symbolSize: 8,
        showSymbol: false,
        itemStyle: { color: pal.primary, borderColor: pal.surface, borderWidth: 2 },
        lineStyle: { color: pal.primary, width: 2.5 },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: `${pal.primary}${mini ? '3D' : '38'}` },
              { offset: 1, color: `${pal.primary}00` },
            ],
          },
        },
        z: 3,
        markLine: mini
          ? undefined
          : {
              silent: true,
              symbol: 'none',
              animation: false,
              data: [{ xAxis: model.todayLabel }],
              lineStyle: { color: pal.textTertiary, type: [2, 3], width: 1, opacity: 0.55 },
              label: {
                formatter: 'hoje',
                position: 'insideEndTop',
                fontFamily: CHART_MONO,
                fontSize: 9,
                color: pal.textTertiary,
              },
            },
      },
      {
        name: 'Cobranças previstas',
        type: 'line',
        data: projectionData,
        // Charges land on a day: the cumulative steps up there, no slope between.
        step: 'end',
        connectNulls: false,
        symbol: 'none',
        showSymbol: true,
        itemStyle: { color: pal.primary, borderColor: pal.surface, borderWidth: 2 },
        lineStyle: { color: pal.primary, width: 2, type: [1, 4], cap: 'round' },
        // Adjacent charge days (or long names) would overprint their chips on a
        // 31-point axis: keep the ones that fit, shed only the colliding ones.
        labelLayout: hideOverlapLabels,
        z: 3,
      },
      ...(baseline
        ? [
            {
              name: capitalize(baseline.label),
              type: 'line' as const,
              data: model.baselineSeries,
              // The steps are real data (day-of-month average) — never smooth them.
              smooth: false,
              symbol: 'none',
              showSymbol: false,
              silent: true,
              // Neutral gray: the reference is "the past", never "your money".
              itemStyle: { color: pal.textTertiary },
              lineStyle: { color: pal.textTertiary, width: 1.5, type: [5, 4], opacity: 0.8 },
              z: 2,
            },
          ]
        : []),
    ],
  };
}
